import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import type { MouseEvent, MouseEventHandler, PropsWithChildren, ReactNode } from 'react'
import { isNonNil } from 'utils'

import { SpriteMap } from '../global'
import { createVariants } from '../lib'
import { Icon } from './Icon'

export const MeatballMenu = ({
  menuItemsWidth = 'w-72',
  color = 'text-gray-600',
  children
}: { menuItemsWidth?: string; color?: string } & PropsWithChildren) => (
  <Menu as="div" className="relative flex">
    <MenuButton
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation() // prevent the click event from reaching ancestors
      }}
    >
      <Icon icon={SpriteMap.MeatballIcon} className={`size-4 ${color}`} />
    </MenuButton>
    <MenuItems
      anchor="bottom end"
      modal={false}
      className={`z-40 flex flex-col rounded-lg bg-white p-2 ring-1 ring-gray-300 drop-shadow-md [--anchor-gap:4px] focus-visible:outline-0 ${menuItemsWidth}`}
    >
      {children}
    </MenuItems>
  </Menu>
)

const menuItemClasses = createVariants({
  base: 'flex-col text-left p-2 rounded',
  variants: {
    color: {
      default: 'text-black hover:bg-gray-200',
      success: 'text-green-700 hover:bg-accent-200',
      danger: 'text-red-700 hover:bg-red-200'
    },
    disabled: {
      true: 'cursor-not-allowed opacity-50'
    }
  },
  defaultVariants: {
    color: 'default'
  }
})

export const MeatballMenuItem = ({
  title,
  onClick,
  icon,
  description,
  color,
  disabled
}: {
  title: string
  description?: string
  onClick: MouseEventHandler<HTMLButtonElement>
  icon?: ReactNode
  color?: 'success' | 'danger'
  disabled?: boolean
}) => (
  <MenuItem
    key={title}
    as="button"
    className={menuItemClasses({ color, disabled })}
    onClick={(e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation() // prevent the click event from reaching ancestors
      onClick(e)
    }}
    disabled={disabled}
  >
    <span className="pan-body-bold flex w-full items-center gap-1">
      {title}
      {icon}
    </span>
    {isNonNil(description) && <span className="pan-detail">{description}</span>}
  </MenuItem>
)
